import { useEffect, useState } from "react";
import {
  Copy, Check, Mail, Trash2, UserCog, ShieldAlert, Crown, Plus, Sparkles, RotateCcw,
  Webhook, AlertTriangle, EyeOff, Eye,
} from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { fmtDate } from "../lib/constants";

interface Member { id: string; name: string; email: string; role: "owner" | "admin" | "member"; created_at: number }
interface Invitation { id: string; email: string; role: string; token: string; accepted_at: number | null; expires_at: number; created_at: number }

const ROLE_LABEL: Record<string, string> = {
  owner: "Owner",
  admin: "Admin",
  member: "Member",
};

export default function Settings() {
  const { user } = useAuth();
  const [members, setMembers] = useState<Member[]>([]);
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<"admin" | "member">("member");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [copiedToken, setCopiedToken] = useState<string | null>(null);

  const isOwner = user?.role === "owner";
  const canInvite = user?.role === "owner" || user?.role === "admin";

  const [demoBusy, setDemoBusy] = useState<"seed" | "reset" | null>(null);
  const [demoMsg, setDemoMsg] = useState<string | null>(null);

  const seedDemo = async () => {
    if (!confirm("Add 2 leaders, 2 groups, and 12 demo people to this workspace? Safe to run multiple times — it will keep adding.")) return;
    setDemoBusy("seed"); setDemoMsg(null);
    try {
      const r = await api.post<{ inserted: { leaders: number; groups: number; people: number } }>("/demo/seed");
      setDemoMsg(`Added ${r.inserted.leaders} leaders, ${r.inserted.groups} groups, ${r.inserted.people} people.`);
    } catch (e: any) {
      setDemoMsg(`Error: ${e.message}`);
    } finally {
      setDemoBusy(null);
    }
  };

  const resetDemo = async () => {
    if (!confirm("Wipe ALL people, groups, leaders, notes, and activity in this workspace? This cannot be undone. Members and invitations stay.")) return;
    setDemoBusy("reset"); setDemoMsg(null);
    try {
      await api.post("/demo/reset");
      setDemoMsg("Workspace data reset. People, groups, leaders, notes, activity all cleared.");
    } catch (e: any) {
      setDemoMsg(`Error: ${e.message}`);
    } finally {
      setDemoBusy(null);
    }
  };

  const load = async () => {
    const [m, i] = await Promise.all([
      api.get<{ members: Member[] }>("/team/members"),
      api.get<{ invitations: Invitation[] }>("/invitations"),
    ]);
    setMembers(m.members);
    setInvitations(i.invitations);
  };

  useEffect(() => { load(); }, []);

  const submitInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      await api.post("/invitations", { email: inviteEmail, role: inviteRole });
      setInviteEmail("");
      setInviteRole("member");
      await load();
    } catch (e: any) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (id: string) => {
    if (!confirm("Revoke this invitation?")) return;
    await api.del(`/invitations/${id}`);
    load();
  };

  const inviteUrl = (token: string) => `${window.location.origin}/invite/${token}`;

  const copy = async (token: string) => {
    await navigator.clipboard.writeText(inviteUrl(token));
    setCopiedToken(token);
    setTimeout(() => setCopiedToken(null), 1500);
  };

  const changeRole = async (memberId: string, role: string) => {
    try {
      await api.patch(`/team/members/${memberId}/role`, { role });
      load();
    } catch (e: any) {
      alert(e.message);
    }
  };

  const removeMember = async (m: Member) => {
    if (!confirm(`Remove ${m.name} from the workspace? They'll lose access immediately.`)) return;
    try {
      await api.del(`/team/members/${m.id}`);
      load();
    } catch (e: any) { alert(e.message); }
  };

  const pending = invitations.filter((i) => !i.accepted_at);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Workspace Settings</h1>
        <p className="text-ink-500 text-sm">Manage your organisation, team members, and invitations</p>
      </div>

      <section className="card p-6">
        <h2 className="font-semibold mb-4">Workspace</h2>
        <div className="grid sm:grid-cols-2 gap-4 text-sm">
          <div>
            <div className="text-ink-500 text-xs uppercase tracking-wide">Organisation name</div>
            <div className="font-medium mt-1">{user?.org_name}</div>
          </div>
          <div>
            <div className="text-ink-500 text-xs uppercase tracking-wide">Workspace slug</div>
            <div className="font-mono text-ink-700 mt-1">{user?.org_slug}</div>
          </div>
        </div>
      </section>

      {isOwner && <WebhookPanel />}

      {canInvite && (
        <section className="card p-6">
          <h2 className="font-semibold mb-1">Invite someone to your workspace</h2>
          <p className="text-sm text-ink-500 mb-4">
            We'll generate an invite link you can share by message or email. They'll set their own name and password.
          </p>
          <form onSubmit={submitInvite} className="grid sm:grid-cols-[1fr_140px_auto] gap-3">
            <div>
              <label className="label">Email address</label>
              <input
                className="input"
                type="email"
                required
                placeholder="name@church.org"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
              />
            </div>
            <div>
              <label className="label">Role</label>
              <select className="input" value={inviteRole} onChange={(e) => setInviteRole(e.target.value as any)}>
                <option value="member">Member</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            <div className="flex items-end">
              <button className="btn-primary" disabled={busy}>
                <Plus className="h-4 w-4" /> {busy ? "Creating…" : "Create invite"}
              </button>
            </div>
          </form>
          {err && <div className="text-sm text-brand mt-2">{err}</div>}
          <div className="text-xs text-ink-500 mt-3">
            <strong>Member</strong> can view and edit ministry data. <strong>Admin</strong> can additionally invite teammates.
            Only the workspace owner can change roles or remove members.
          </div>
        </section>
      )}

      <section className="card p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-semibold">Pending invitations ({pending.length})</h2>
        </div>
        {pending.length === 0 ? (
          <div className="text-sm text-ink-500">No pending invitations.</div>
        ) : (
          <div className="divide-y divide-ink-100">
            {pending.map((inv) => {
              const expired = inv.expires_at < Math.floor(Date.now() / 1000);
              return (
                <div key={inv.id} className="py-3 flex items-center gap-3 flex-wrap">
                  <div className="flex-1 min-w-[200px]">
                    <div className="flex items-center gap-2 font-medium">
                      <Mail className="h-3.5 w-3.5 text-ink-400" /> {inv.email}
                      <span className="badge bg-ink-100 text-ink-600 ml-1">{ROLE_LABEL[inv.role] ?? inv.role}</span>
                      {expired && <span className="badge bg-amber-100 text-amber-800">Expired</span>}
                    </div>
                    <div className="text-xs text-ink-500 mt-0.5">
                      Created {fmtDate(inv.created_at)} · expires {fmtDate(inv.expires_at)}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      readOnly
                      value={inviteUrl(inv.token)}
                      onClick={(e) => (e.target as HTMLInputElement).select()}
                      className="input w-[280px] font-mono text-xs"
                    />
                    <button onClick={() => copy(inv.token)} className="btn-secondary" title="Copy invite link">
                      {copiedToken === inv.token ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                    </button>
                    {canInvite && (
                      <button onClick={() => revoke(inv.id)} className="btn-ghost text-brand" title="Revoke">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      <section className="card overflow-hidden">
        <div className="px-6 py-4 border-b border-ink-100 flex items-center justify-between">
          <h2 className="font-semibold">Team members ({members.length})</h2>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-ink-50 text-left text-xs font-medium uppercase text-ink-500">
            <tr>
              <th className="px-6 py-3">Name</th>
              <th className="px-6 py-3">Email</th>
              <th className="px-6 py-3">Role</th>
              <th className="px-6 py-3">Joined</th>
              <th className="px-6 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-ink-100">
            {members.map((m) => (
              <tr key={m.id}>
                <td className="px-6 py-3 font-medium">
                  {m.name}
                  {m.id === user?.id && <span className="text-xs text-ink-500 ml-2">(you)</span>}
                </td>
                <td className="px-6 py-3 text-ink-700">{m.email}</td>
                <td className="px-6 py-3">
                  {isOwner ? (
                    <select
                      className="input py-1 text-sm w-32"
                      value={m.role}
                      onChange={(e) => changeRole(m.id, e.target.value)}
                    >
                      <option value="owner">Owner</option>
                      <option value="admin">Admin</option>
                      <option value="member">Member</option>
                    </select>
                  ) : (
                    <RoleBadge role={m.role} />
                  )}
                </td>
                <td className="px-6 py-3 text-ink-500">{fmtDate(m.created_at)}</td>
                <td className="px-6 py-3 text-right">
                  {isOwner && m.id !== user?.id && (
                    <button onClick={() => removeMember(m)} className="text-brand text-xs hover:underline">
                      Remove
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {!canInvite && (
        <div className="card p-4 bg-amber-50 border-amber-200 text-sm text-amber-900 flex items-start gap-2">
          <ShieldAlert className="h-4 w-4 mt-0.5" />
          <div>You're a member of this workspace. To invite teammates or change roles, ask an admin or the owner.</div>
        </div>
      )}

      {isOwner && (
        <section className="card p-6">
          <h2 className="font-semibold mb-1 flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-amber-500" /> Demo data
          </h2>
          <p className="text-sm text-ink-500 mb-4">
            Quickly populate this workspace with realistic dummy people, leaders, and DBS groups so the team can test the flows. You can wipe it clean again anytime.
          </p>
          <div className="flex flex-wrap gap-2">
            <button onClick={seedDemo} disabled={demoBusy !== null} className="btn-primary">
              <Sparkles className="h-4 w-4" /> {demoBusy === "seed" ? "Adding…" : "Add demo data"}
            </button>
            <button onClick={resetDemo} disabled={demoBusy !== null} className="btn-secondary text-brand">
              <RotateCcw className="h-4 w-4" /> {demoBusy === "reset" ? "Resetting…" : "Reset all workspace data"}
            </button>
          </div>
          {demoMsg && (
            <div className={`mt-3 text-sm ${demoMsg.startsWith("Error") ? "text-brand" : "text-green-700"}`}>{demoMsg}</div>
          )}
          <p className="text-xs text-ink-500 mt-3">
            Reset removes all people, groups, leaders, notes, and activity from this workspace. Member accounts and invitations are kept.
          </p>
        </section>
      )}
    </div>
  );
}

function WebhookPanel() {
  const [token, setToken] = useState<string | null | "unloaded">("unloaded");
  const [reveal, setReveal] = useState(false);
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    api.get<{ token: string | null }>("/team/webhook-token")
      .then((r) => setToken(r.token))
      .catch((e) => setErr(e.message));
  }, []);

  const generate = async () => {
    setBusy(true); setErr(null);
    try { const r = await api.post<{ token: string }>("/team/webhook-token"); setToken(r.token); setReveal(true); }
    catch (e: any) { setErr(e.message); }
    finally { setBusy(false); }
  };
  const rotate = async () => {
    if (!confirm("Rotate the webhook URL? Any external system currently using the old URL will stop working until you update it.")) return;
    setBusy(true); setErr(null);
    try { const r = await api.post<{ token: string }>("/team/webhook-token"); setToken(r.token); setReveal(true); }
    catch (e: any) { setErr(e.message); }
    finally { setBusy(false); }
  };
  const revoke = async () => {
    if (!confirm("Revoke the webhook URL completely? Inbound leads will stop arriving until you generate a new one.")) return;
    setBusy(true); setErr(null);
    try { await api.del("/team/webhook-token"); setToken(null); setReveal(false); }
    catch (e: any) { setErr(e.message); }
    finally { setBusy(false); }
  };

  const fullUrl = token && token !== "unloaded" ? `${window.location.origin}/api/inbound/leads/${token}` : "";

  const copy = async () => {
    if (!fullUrl) return;
    await navigator.clipboard.writeText(fullUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <section className="card p-6">
      <h2 className="font-semibold mb-1 flex items-center gap-2">
        <Webhook className="h-4 w-4 text-brand" /> Inbound lead webhook
      </h2>
      <p className="text-sm text-ink-500 mb-4">
        Pipe leads straight from a campaign form (Typeform, Wix, Make.com, Zapier) into your <strong>Leads · Inbox</strong>.
        POST a JSON body with at least <code className="text-xs bg-ink-100 px-1 rounded">name</code>; optional fields:{" "}
        <code className="text-xs bg-ink-100 px-1 rounded">email · phone · source · campaign · notes</code>.
      </p>

      {token === "unloaded" ? (
        <div className="text-sm text-ink-500">Loading…</div>
      ) : token ? (
        <div className="space-y-3">
          <div className="rounded-md bg-ink-50 ring-1 ring-ink-200 p-3 flex items-center gap-2">
            <span className="text-xs text-ink-500 uppercase tracking-wide shrink-0 hidden sm:inline">POST</span>
            <input
              readOnly
              value={reveal ? fullUrl : fullUrl.replace(/(\/inbound\/leads\/).+/, "$1•••••••••••••••••••")}
              onClick={(e) => (e.target as HTMLInputElement).select()}
              className="flex-1 bg-transparent text-xs font-mono outline-none"
            />
            <button onClick={() => setReveal(!reveal)} className="btn-ghost p-1.5" title={reveal ? "Hide" : "Reveal"}>
              {reveal ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
            <button onClick={copy} className="btn-secondary py-1.5 px-2.5 text-xs" title="Copy URL">
              {copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
            </button>
          </div>
          <div className="flex gap-2">
            <button onClick={rotate} disabled={busy} className="btn-ghost text-xs text-amber-700">
              <RotateCcw className="h-3.5 w-3.5" /> Rotate URL
            </button>
            <button onClick={revoke} disabled={busy} className="btn-ghost text-xs text-brand">
              <Trash2 className="h-3.5 w-3.5" /> Revoke
            </button>
          </div>
          <div className="text-xs text-ink-500 mt-2">
            <strong>Example body:</strong>{" "}
            <code className="font-mono">{"{ \"name\": \"Ruth Anderson\", \"email\": \"ruth@example.com\", \"campaign\": \"Stories of Hope\" }"}</code>
          </div>
        </div>
      ) : (
        <div>
          <button onClick={generate} disabled={busy} className="btn-primary">
            <Plus className="h-4 w-4" /> {busy ? "Generating…" : "Generate webhook URL"}
          </button>
          <div className="text-xs text-ink-500 mt-2 flex items-center gap-1.5">
            <AlertTriangle className="h-3.5 w-3.5 text-amber-600" />
            Treat the URL as a secret — anyone with it can post leads into your workspace.
          </div>
        </div>
      )}
      {err && <div className="text-sm text-brand mt-2">{err}</div>}
    </section>
  );
}

function RoleBadge({ role }: { role: string }) {
  const Icon = role === "owner" ? Crown : role === "admin" ? UserCog : null;
  const cls =
    role === "owner" ? "bg-brand-50 text-brand-700" :
    role === "admin" ? "bg-blue-100 text-blue-800" :
    "bg-ink-100 text-ink-700";
  return (
    <span className={`badge ${cls}`}>
      {Icon && <Icon className="h-3 w-3 mr-1" />}
      {ROLE_LABEL[role] ?? role}
    </span>
  );
}
